import React, { useState, useEffect } from 'react';
import { Carousel } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import 'swiper/swiper-bundle.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import SEO from '../../Components/SEO';
import Breadcrumb from '../../Components/Breadcrumb';
import YouTubeVideos from '../../Components/YouTubeVideos';
import videoData from '../../Shared/Activities/Data/videos/video.json';
import { addTooltipsToText } from '../../Shared/tooltipUtils';
import { ActivityLayout } from '../../Components/activity-layout';
import ActivityFilter from '../../Shared/activityFilter';
import videosData from './videos-data';
import './videos.css';

const videosImg = '/Images/listening.webp';

const Videos = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const titleParam = searchParams.get('title');
  const isPlayerView = titleParam !== null;

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showVocab, setShowVocab] = useState(false);
  const [tooltippedTranscript, setTooltippedTranscript] = useState('');

  // Player view logic - find video index from title param
  useEffect(() => {
    const title = searchParams.get('title');
    if (title) {
      const videoIndex = videoData.findIndex((video) => video.title === title);
      if (videoIndex !== -1) {
        setCurrentVideoIndex(videoIndex);
        setAnswers(Array(videoData[videoIndex].questions.length).fill(''));
      }
    }
  }, [location]);

  useEffect(() => {
    if (isPlayerView) {
      window.scrollTo(0, 0);
    }
  }, [isPlayerView]);

  useEffect(() => {
    if (showTranscript) {
      const transcriptText = videoData[currentVideoIndex].transcript.join(' ');
      const vocabulary = videoData[currentVideoIndex].vocabulary;
      if (vocabulary && vocabulary.length > 0) {
        setTooltippedTranscript(addTooltipsToText(transcriptText, vocabulary));
      } else {
        setTooltippedTranscript(transcriptText);
      }
    }
  }, [currentVideoIndex, showTranscript]);

  const handlePreviousVideo = () => {
    const prevIndex = (currentVideoIndex - 1 + videoData.length) % videoData.length;
    setCurrentVideoIndex(prevIndex);
    setAnswers(Array(videoData[prevIndex].questions.length).fill(''));
    setShowResults(false);
  };

  const handleNextVideo = () => {
    const nextIndex = (currentVideoIndex + 1) % videoData.length;
    setCurrentVideoIndex(nextIndex);
    setAnswers(Array(videoData[nextIndex].questions.length).fill(''));
    setShowResults(false);
  };

  const handleAnswerChange = (questionIndex, choice) => {
    const updatedAnswers = [...answers];
    updatedAnswers[questionIndex] = choice;
    setAnswers(updatedAnswers);
    setShowResults(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowResults(true);
  };

  const isCorrect = (questionIndex) => {
    return answers[questionIndex] === videoData[currentVideoIndex].questions[questionIndex].correctAnswer;
  };

  const getResultIcon = (questionIndex, choiceIndex) => {
    if (showResults && isCorrect(questionIndex) && answers[questionIndex] === videoData[currentVideoIndex].questions[questionIndex].choices[choiceIndex]) {
      return <span style={{ color: 'green' }}>&#10003;</span>;
    } else if (showResults && !isCorrect(questionIndex) && answers[questionIndex] === videoData[currentVideoIndex].questions[questionIndex].choices[choiceIndex]) {
      return <span style={{ color: 'red' }}>&#10005;</span>;
    } else {
      return null;
    }
  };

  const toggleTranscript = () => setShowTranscript((p) => !p);
  const closeTranscript = () => setShowTranscript(false);
  const toggleVocab = () => setShowVocab((p) => !p);
  const closeVocab = () => setShowVocab(false);

  const currentVideo = videoData[currentVideoIndex];

  // ===== PLAYER VIEW (unchanged) =====
  if (isPlayerView) {
    return (
      <div className="videos-page">
        <div className="videos-player-screen">
          <div className="videos-player-header">
            <button type="button" className="videos-back-btn" onClick={() => navigate('/videos')}>
              ← All Videos
            </button>
            <div className="videos-player-title">
              <h1>{currentVideo.title}</h1>
              <div className="videos-player-meta">
                <span>{currentVideo.topic}</span>
                <span className={`video-level-badge level-${currentVideo.level.toLowerCase()}`}>
                  {currentVideo.level}
                </span>
              </div>
            </div>
          </div>

          <div className="videos-player-content">
            <div className="video-container">
              <YouTubeVideos videoId={currentVideo.videoId} />
            </div>
            <div className="transcript-container">
              <button type="button" className="checkbtn" onClick={toggleTranscript}>Transcript</button>
              <button type="button" className="checkbtn" onClick={toggleVocab}>Vocabulary</button>
            </div>

            {showVocab && (
              <div className="overlay" onClick={closeVocab}>
                <div className="transcript-overlay" onClick={(e) => e.stopPropagation()}>
                  <div className="vocabulary-text">
                    <ul>
                      {currentVideo.vocabulary.map((item, index) => {
                        const [word, translation] = item.split(' - ').map(str => str.trim());
                        return (
                          <li key={index}>
                            <strong>{word}</strong> - {translation}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {showTranscript && (
              <div className="overlay" onClick={closeTranscript}>
                <div className="transcript-overlay" onClick={(e) => e.stopPropagation()}>
                  <div
                    className="transcript-text"
                    dangerouslySetInnerHTML={{ __html: tooltippedTranscript }}
                  />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="question-container">
                <Carousel interval={null} wrap={false} controls={true} indicators={false} pause={'hover'}>
                  {currentVideo.questions.map((questionObj, questionIndex) => (
                    <Carousel.Item key={questionIndex}>
                      <div className="question-container">
                        <div className="question">{questionObj.number}. {questionObj.question}</div>
                        <div className="choices-container">
                          {questionObj.choices.map((choice, choiceIndex) => (
                            <label key={choiceIndex}>
                              <input
                                type="radio"
                                value={choice}
                                checked={answers[questionIndex] === choice}
                                onChange={() => handleAnswerChange(questionIndex, choice)}
                              />
                              {choice}
                              {getResultIcon(questionIndex, choiceIndex)}
                            </label>
                          ))}
                        </div>
                      </div>
                      <button type="submit" className="checkbtn">Check Answer</button>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            </form>

            <div className="video-nav-btns">
              <button type="button" className="checkbtn" onClick={handlePreviousVideo}>
                <i className="fas fa-arrow-left"></i> Previous
              </button>
              <button type="button" className="checkbtn" onClick={handleNextVideo}>
                Next <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== BROWSE VIEW (with ActivityFilter) =====
  return (
    <ActivityLayout
      title="Videos"
      image={videosImg}
      intro="Here is a collection of videos sorted by topic and level."
    >
      <SEO
        title='English Video Exercises for Listening and Speaking'
        description='A collection of videos to practice listening comprehension'
        name='English Exam Exercises'
        type='article'
        image={videosImg}
      />
      <Breadcrumb />
      <ActivityFilter
        data={videosData}
        getLink={(item) => `/videos?title=${encodeURIComponent(item.title)}`}
        ctaLabel="Watch"
        resultsLabel="videos"
        itemsPerPage={9}
      />
    </ActivityLayout>
  );
};

export default Videos;